/**
 * Marquee.js - A Vanilla JavaScript implementation of a continuous scrolling marquee.
 */
class Marquee {
  constructor(element, options) {
    this.element = element;
    this.options = {
      duration: options.duration || 5000,
      gap: options.gap || 0,
      direction: options.direction || 'left',
      duplicated: options.duplicated !== false,
      startVisible: options.startVisible !== false,
      pauseOnHover: options.pauseOnHover || false
    };

    this.wrapperClass = 'js-marquee-wrapper';
    this.contentClass = 'js-marquee';
    this.animationName = `marqueeAnimation-${Math.floor(Math.random() * 1e7)}`;

    this.init();
  }

  init() {
    if (!this.options.duplicated) return;

    this.prepareDOM();
    this.calculateDimensions();
    this.createCSSAnimation();
    this.bindHoverEvents();
  }

  prepareDOM() {
    const originalContent = this.element.innerHTML;

    const content1 = document.createElement('div');
    content1.className = this.contentClass;
    content1.innerHTML = originalContent;
    content1.style.marginRight = `${this.options.gap}px`;

    const content2 = content1.cloneNode(true);

    const wrapper = document.createElement('div');
    wrapper.className = this.wrapperClass;
    wrapper.style.display = 'flex';
    wrapper.appendChild(content1);
    wrapper.appendChild(content2);

    this.element.innerHTML = '';
    this.element.appendChild(wrapper);

    this.wrapper = wrapper;
    this.content1 = content1;
  }

  calculateDimensions() {
    this.contentWidth = this.content1.offsetWidth + this.options.gap;
    const totalContentWidth = this.contentWidth * 2;
    this.wrapper.style.width = `${totalContentWidth}px`;
  }

  createCSSAnimation() {
    const durationSeconds = this.options.duration / 1000;
    const distance = this.contentWidth;

    let from;
    let to;

    if (this.options.direction === 'left') {
      from = '0px';
      to = `-${distance}px`;
    } else {
      from = `-${distance}px`;
      to = '0px';
      this.wrapper.style.transform = `translateX(${from})`;
    }

    const keyframes = `
      @keyframes ${this.animationName} {
        0% { transform: translateX(${from}); }
        100% { transform: translateX(${to}); }
      }
    `;

    let styleSheet = document.getElementById('marquee-styles');
    if (!styleSheet) {
      styleSheet = document.createElement('style');
      styleSheet.id = 'marquee-styles';
      document.head.appendChild(styleSheet);
    }

    styleSheet.innerHTML += keyframes;
    this.wrapper.style.animation = `${this.animationName} ${durationSeconds}s linear infinite`;
  }

  bindHoverEvents() {
    if (!this.options.pauseOnHover) return;

    this.element.addEventListener('mouseenter', () => {
      this.wrapper.style.animationPlayState = 'paused';
    });

    this.element.addEventListener('mouseleave', () => {
      this.wrapper.style.animationPlayState = 'running';
    });
  }
}

function initMarquee(selector, options = {}) {
  const elements = document.querySelectorAll(selector);
  if (!elements.length) return;

  const width = window.innerWidth;
  const isMobile = width < 750;

  const defaultOptions = {
    duration: 80000,
    gap: 0,
    direction: 'left',
    duplicated: true,
    startVisible: true,
    pauseOnHover: false
  };

  const mobileOptions = options.mobile || {};
  const desktopOptions = options.desktop || {};

  const finalOptions = {
    ...defaultOptions,
    ...(isMobile ? mobileOptions : desktopOptions)
  };

  elements.forEach((el) => {
    if (!el.dataset.marqueeInitialized) {
      new Marquee(el, finalOptions);
      el.dataset.marqueeInitialized = 'true';
    }
  });
}

window.initMarquee = initMarquee;
